/**
 * PDP Countdown Timer
 * Handles countdown timers for product detail pages using data attributes
 */

class PDPCountdownTimer {
  constructor() {
    this.timers = [];
    this.lowTimeThreshold = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    this.init();
  }

  init() {
    // Find all countdown timer elements on the page
    const timerElements = document.querySelectorAll('.pdp-sale-timer[data-sale-end-date]');
    
    timerElements.forEach(timerElement => {
      this.createTimer(timerElement);
    });
  }

  createTimer(timerElement) {
    const endDate = timerElement.getAttribute('data-sale-end-date');
    
    if (!endDate) {
      console.warn('PDP Countdown Timer: No end date found for timer element');
      return;
    }

    const timer = {
      element: timerElement,
      endDate: new Date(endDate).getTime(),
      interval: null,
      hoursElement: timerElement.querySelector('[data-sale-timer-type="hours"] .pdp-sale-timer-item-value'),
      minutesElement: timerElement.querySelector('[data-sale-timer-type="minutes"] .pdp-sale-timer-item-value'),
      secondsElement: timerElement.querySelector('[data-sale-timer-type="seconds"] .pdp-sale-timer-item-value')
    };

    // Validate that all required elements exist
    if (!timer.hoursElement || !timer.minutesElement || !timer.secondsElement) {
      console.warn('PDP Countdown Timer: Required timer elements not found');
      return;
    }

    this.timers.push(timer);
    this.startTimer(timer);
  }

  startTimer(timer) {
    // Update immediately
    this.updateTimer(timer);
    
    // Then update every second
    timer.interval = setInterval(() => {
      this.updateTimer(timer);
    }, 1000);
  }

  updateTimer(timer) {
    const now = new Date().getTime();
    const distance = timer.endDate - now;

    // If countdown is finished
    if (distance < 0) {
      this.stopTimer(timer);
      this.setExpiredState(timer);
      return;
    }

    // Check if we're in low time mode (less than 24 hours)
    if (distance <= this.lowTimeThreshold && !timer.element.classList.contains('pdp-sale-timer--low-time')) {
      timer.element.classList.add('pdp-sale-timer--low-time');
      timer.element.dispatchEvent(new CustomEvent('countdown:low-time', {
        detail: { timer: timer, timeRemaining: distance }
      }));
    }

    // Calculate time units - show total hours (days * 24 + hours)
    const totalHours = Math.floor(distance / (1000 * 60 * 60));
    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((distance % (1000 * 60)) / 1000);

    // Update the display with zero-padded values
    this.setTimerValue(timer.hoursElement, totalHours);
    this.setTimerValue(timer.minutesElement, minutes);
    this.setTimerValue(timer.secondsElement, seconds);
  }

  setTimerValue(element, value) {
    if (element) {
      element.textContent = value.toString().padStart(2, '0');
    }
  }

  stopTimer(timer) {
    if (timer.interval) {
      clearInterval(timer.interval);
      timer.interval = null;
    }
  }

  setExpiredState(timer) {
    // Set all values to 00
    this.setTimerValue(timer.hoursElement, 0);
    this.setTimerValue(timer.minutesElement, 0);
    this.setTimerValue(timer.secondsElement, 0);

    // Add expired class
    timer.element.classList.add('pdp-sale-timer--expired');
    timer.element.classList.remove('pdp-sale-timer--low-time');
    
    // Dispatch custom event for other scripts to listen to
    timer.element.dispatchEvent(new CustomEvent('countdown:expired', {
      detail: { timer: timer }
    }));
  }

  // Public method to stop all timers
  destroy() {
    this.timers.forEach(timer => {
      this.stopTimer(timer);
    });
    this.timers = [];
  }

  // Public method to restart all timers
  restart() {
    this.destroy();
    this.init();
  }

  // Public method to set low time threshold
  setLowTimeThreshold(threshold) {
    this.lowTimeThreshold = threshold;
  }

  // Public method to get timer status
  getTimerStatus(timerElement) {
    const timer = this.timers.find(t => t.element === timerElement);
    if (!timer) return null;

    const now = new Date().getTime();
    const distance = timer.endDate - now;
    
    return {
      isExpired: distance < 0,
      isLowTime: distance <= this.lowTimeThreshold && distance > 0,
      timeRemaining: distance,
      hours: Math.floor(distance / (1000 * 60 * 60)), // Total hours (days * 24 + hours)
      minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((distance % (1000 * 60)) / 1000)
    };
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.pdpCountdownTimer = new PDPCountdownTimer();
});

// Also initialize for dynamic content (if using AJAX or similar)
document.addEventListener('shopify:section:load', () => {
  if (window.pdpCountdownTimer) {
    window.pdpCountdownTimer.restart();
  } else {
    window.pdpCountdownTimer = new PDPCountdownTimer();
  }
});

// Export for module systems if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = PDPCountdownTimer;
} 